import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  ArrowLeft, 
  Search, 
  BookOpen, 
  Lightbulb,
  Brain,
  Lock,
  LogIn,
  Bookmark,
  GraduationCap,
  FileText,
  Zap,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Trophy,
  X,
  BookMarked,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function DictionaryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isPremium, loading: authLoading, authChecked } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("acronyms");
  
  // Data states - loaded from API
  const [acronyms, setAcronyms] = useState([]);
  const [memoryAids, setMemoryAids] = useState([]);
  const [coreConcepts, setCoreConcepts] = useState([]);
  const [dictionary, setDictionary] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [stats, setStats] = useState({});
  
  // Quiz Me states
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);

  // Fetch dictionary data from API
  useEffect(() => {
    const fetchDictionaryData = async () => {
      try {
        setLoadingData(true);
        const response = await axios.get(`${API}/dictionary/all`);
        const data = response.data;
        
        setAcronyms(data.acronyms || []);
        setMemoryAids(data.memory_aids || []);
        
        // Convert core_concepts from object to array format
        const conceptsObj = data.core_concepts || {};
        const conceptsArray = Object.entries(conceptsObj).map(([category, concepts]) => ({
          category,
          concepts: concepts || []
        }));
        setCoreConcepts(conceptsArray);
        
        setDictionary(data.terms || []);
        setStats(data.stats || {});
      } catch (error) {
        console.error("Failed to load dictionary data:", error);
      } finally {
        setLoadingData(false);
      }
    };
    
    if (isAuthenticated && isPremium) {
      fetchDictionaryData();
    }
  }, [isAuthenticated, isPremium]);

  // Generate quiz questions based on current tab
  const generateQuiz = () => {
    let questions = [];
    const numQuestions = 5;
    
    if (activeTab === "acronyms") {
      // Shuffle and pick random acronyms
      const shuffled = [...acronyms].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numQuestions);
      
      questions = selected.map(item => {
        // Get 3 random wrong answers
        const otherAcronyms = acronyms.filter(a => a.acronym !== item.acronym);
        const wrongAnswers = otherAcronyms
          .sort(() => Math.random() - 0.5)
          .slice(0, 3)
          .map(a => a.full);
        
        // Mix correct answer with wrong answers
        const allOptions = [item.full, ...wrongAnswers].sort(() => Math.random() - 0.5);
        
        return {
          question: `What does "${item.acronym}" stand for?`,
          correctAnswer: item.full,
          options: allOptions,
          explanation: item.description
        };
      });
    } else if (activeTab === "dictionary") {
      // Shuffle and pick random dictionary terms
      const shuffled = [...dictionary].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numQuestions);
      
      questions = selected.map(item => {
        // Get 3 random wrong definitions
        const otherTerms = dictionary.filter(t => t.term !== item.term);
        const wrongAnswers = otherTerms
          .sort(() => Math.random() - 0.5)
          .slice(0, 3)
          .map(t => t.definition.length > 80 ? t.definition.substring(0, 80) + "..." : t.definition);
        
        const correctDef = item.definition.length > 80 ? item.definition.substring(0, 80) + "..." : item.definition;
        const allOptions = [correctDef, ...wrongAnswers].sort(() => Math.random() - 0.5);
        
        return {
          question: `What is the definition of "${item.term}"?`,
          correctAnswer: correctDef,
          options: allOptions,
          explanation: item.definition
        };
      });
    } else if (activeTab === "memory") {
      // Quiz on memory aid mnemonics
      const shuffled = [...memoryAids].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, Math.min(numQuestions, memoryAids.length));
      
      questions = selected.map(item => {
        const otherAids = memoryAids.filter(m => m.mnemonic !== item.mnemonic);
        const wrongAnswers = otherAids
          .sort(() => Math.random() - 0.5)
          .slice(0, 3)
          .map(m => m.mnemonic);
        
        const allOptions = [item.mnemonic, ...wrongAnswers].sort(() => Math.random() - 0.5);
        
        return {
          question: `What mnemonic helps you remember "${item.title}"?`,
          correctAnswer: item.mnemonic,
          options: allOptions,
          explanation: item.breakdown
        };
      });
    } else if (activeTab === "concepts") {
      // Quiz on core concepts
      const allConcepts = coreConcepts.flatMap(cat => cat.concepts);
      const shuffled = allConcepts.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numQuestions);
      
      questions = selected.map(item => {
        const otherConcepts = allConcepts.filter(c => c.term !== item.term);
        const wrongAnswers = otherConcepts
          .sort(() => Math.random() - 0.5)
          .slice(0, 3)
          .map(c => c.term);
        
        const allOptions = [item.term, ...wrongAnswers].sort(() => Math.random() - 0.5);
        
        return {
          question: item.definition,
          correctAnswer: item.term,
          options: allOptions,
          explanation: `${item.term}: ${item.definition}`
        };
      });
    }
    
    setQuizQuestions(questions);
    setCurrentQuizIndex(0);
    setSelectedAnswer(null);
    setShowAnswer(false);
    setQuizScore(0);
    setQuizComplete(false);
    setShowQuiz(true);
  };

  const handleAnswerSelect = (answer) => {
    if (showAnswer) return;
    setSelectedAnswer(answer);
    setShowAnswer(true);
    if (answer === quizQuestions[currentQuizIndex].correctAnswer) {
      setQuizScore(prev => prev + 1);
    }
  };

  const nextQuestion = () => {
    if (currentQuizIndex < quizQuestions.length - 1) {
      setCurrentQuizIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowAnswer(false);
    } else {
      setQuizComplete(true);
    }
  };

  const closeQuiz = () => {
    setShowQuiz(false);
    setQuizQuestions([]);
    setCurrentQuizIndex(0);
    setSelectedAnswer(null);
    setShowAnswer(false);
    setQuizScore(0);
    setQuizComplete(false);
  };

  // Filter functions
  const filteredAcronyms = acronyms.filter(item =>
    item.acronym.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.full.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDictionary = dictionary.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show login required screen if not authenticated or not premium
  if (authChecked && !authLoading && (!isAuthenticated || !isPremium)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {isAuthenticated ? "Subscription Required" : "Login Required"}
            </h1>
            <p className="text-slate-600 mb-6">
              {isAuthenticated 
                ? "Subscribe to access the ABA Prep Lab Dictionary and all study resources."
                : "Create an account and subscribe to access the dictionary and track your progress."
              }
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {isAuthenticated ? "Subscribe Now - $7.00/month" : "Sign In / Create Account"}
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/quizzes")}
                data-testid="back-btn"
                className="text-slate-600 hover:text-teal-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-slate-100">ABA Prep Lab</span>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <BookOpen className="w-3 h-3 mr-1" />
              Dictionary
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="page-title">
                ABA Prep Lab Dictionary
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Comprehensive ABA terminology, acronyms, and study aids
              </p>
            </div>
          </div>
        </motion.div>

        {/* Search and Quiz Me */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search terms, acronyms, or definitions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-6 text-lg"
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={generateQuiz}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-6 px-6"
              data-testid="quiz-me-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Quiz Me!
            </Button>
          </div>
        </motion.div>

        {/* Quiz Modal */}
        <AnimatePresence>
          {showQuiz && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={(e) => e.target === e.currentTarget && closeQuiz()}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                data-testid="quiz-modal"
              >
                {/* Quiz Header */}
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">Quick Quiz</h2>
                        <p className="text-white/80 text-sm">
                          {quizComplete 
                            ? "Quiz Complete!" 
                            : `Question ${currentQuizIndex + 1} of ${quizQuestions.length}`
                          }
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeQuiz}
                      className="text-white hover:bg-white/20"
                      data-testid="close-quiz-btn"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  {!quizComplete && (
                    <div className="mt-4 bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-white h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuizIndex + 1) / quizQuestions.length) * 100}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Quiz Content */}
                <div className="p-6">
                  {quizComplete ? (
                    /* Quiz Results */
                    <div className="text-center py-8">
                      <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                        quizScore >= quizQuestions.length * 0.8 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : quizScore >= quizQuestions.length * 0.5 
                          ? 'bg-amber-100 dark:bg-amber-900/30'
                          : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        <Trophy className={`w-10 h-10 ${
                          quizScore >= quizQuestions.length * 0.8 
                            ? 'text-green-600' 
                            : quizScore >= quizQuestions.length * 0.5 
                            ? 'text-amber-600'
                            : 'text-red-600'
                        }`} />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                        {quizScore >= quizQuestions.length * 0.8 
                          ? "Excellent Work!" 
                          : quizScore >= quizQuestions.length * 0.5 
                          ? "Good Effort!"
                          : "Keep Practicing!"
                        }
                      </h3>
                      <p className="text-4xl font-bold text-violet-600 mb-2">
                        {quizScore} / {quizQuestions.length}
                      </p>
                      <p className="text-slate-600 dark:text-slate-400 mb-8">
                        {Math.round((quizScore / quizQuestions.length) * 100)}% Correct
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={generateQuiz}
                          className="bg-violet-600 hover:bg-violet-700 text-white"
                          data-testid="try-again-btn"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Try Again
                        </Button>
                        <Button
                          variant="outline"
                          onClick={closeQuiz}
                          data-testid="back-to-dictionary-btn"
                        >
                          Back to Dictionary
                        </Button>
                      </div>
                    </div>
                  ) : quizQuestions.length > 0 && (
                    /* Quiz Question */
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-6" data-testid="quiz-question">
                        {quizQuestions[currentQuizIndex].question}
                      </h3>
                      
                      <div className="space-y-3">
                        {quizQuestions[currentQuizIndex].options.map((option, index) => {
                          const isCorrect = option === quizQuestions[currentQuizIndex].correctAnswer;
                          const isSelected = option === selectedAnswer;
                          
                          let buttonClass = "w-full p-4 text-left rounded-xl border-2 transition-all ";
                          
                          if (showAnswer) {
                            if (isCorrect) {
                              buttonClass += "border-green-500 bg-green-50 dark:bg-green-900/20";
                            } else if (isSelected && !isCorrect) {
                              buttonClass += "border-red-500 bg-red-50 dark:bg-red-900/20";
                            } else {
                              buttonClass += "border-slate-200 dark:border-slate-700 opacity-50";
                            }
                          } else {
                            buttonClass += isSelected 
                              ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                              : "border-slate-200 dark:border-slate-700 hover:border-violet-300 hover:bg-violet-50/50";
                          }
                          
                          return (
                            <button
                              key={index}
                              onClick={() => handleAnswerSelect(option)}
                              disabled={showAnswer}
                              className={buttonClass}
                              data-testid={`quiz-option-${index}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-slate-700 dark:text-slate-300">{option}</span>
                                {showAnswer && isCorrect && (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                )}
                                {showAnswer && isSelected && !isCorrect && (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Explanation */}
                      {showAnswer && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-6 p-4 bg-slate-100 dark:bg-slate-700 rounded-xl"
                        >
                          <div className="flex items-start gap-2">
                            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100 mb-1">Explanation</p>
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {quizQuestions[currentQuizIndex].explanation}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Next Button */}
                      {showAnswer && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="mt-6"
                        >
                          <Button
                            onClick={nextQuestion}
                            className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6"
                            data-testid="next-question-btn"
                          >
                            {currentQuizIndex < quizQuestions.length - 1 ? "Next Question" : "See Results"}
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading State */}
        {loadingData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <Loader2 className="w-12 h-12 text-violet-600 animate-spin mb-4" />
            <p className="text-lg text-slate-600 dark:text-slate-400">Loading dictionary content...</p>
          </motion.div>
        )}

        {/* Tabs */}
        {!loadingData && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            <TabsTrigger 
              value="acronyms" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-acronyms"
            >
              <FileText className="w-4 h-4 mr-2" />
              Acronyms
            </TabsTrigger>
            <TabsTrigger 
              value="memory" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-memory"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Memory Aids
            </TabsTrigger>
            <TabsTrigger 
              value="concepts" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-concepts"
            >
              <Brain className="w-4 h-4 mr-2" />
              Core Concepts
            </TabsTrigger>
            <TabsTrigger 
              value="dictionary" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-dictionary"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Full Dictionary
            </TabsTrigger>
          </TabsList>

          {/* Acronyms Tab */}
          <TabsContent value="acronyms">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filteredAcronyms.map((item, index) => (
                <motion.div
                  key={item.acronym}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow"
                  data-testid={`acronym-${item.acronym}`}
                >
                  <div className="flex items-start gap-3">
                    <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 font-bold text-lg px-3 py-1">
                      {item.acronym}
                    </Badge>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                        {item.full}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
              {filteredAcronyms.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No acronyms found matching &ldquo;{searchTerm}&rdquo;</p>
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Memory Aids Tab */}
          <TabsContent value="memory">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {memoryAids.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700"
                  data-testid={`memory-aid-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100 mb-2">
                        {item.title}
                      </h3>
                      <div className="mb-3">
                        <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 text-lg px-4 py-2 font-bold">
                          {item.mnemonic}
                        </Badge>
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 font-medium mb-2">
                        {item.breakdown}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {item.explanation}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          {/* Core Concepts Tab */}
          <TabsContent value="concepts">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {coreConcepts.map((category, catIndex) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: catIndex * 0.1 }}
                  className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 px-6 py-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Brain className="w-5 h-5" />
                      {category.category}
                    </h3>
                  </div>
                  <div className="p-4">
                    <Accordion type="single" collapsible className="space-y-2">
                      {category.concepts.map((concept, index) => (
                        <AccordionItem 
                          key={concept.term} 
                          value={concept.term}
                          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4"
                        >
                          <AccordionTrigger className="text-left font-semibold text-slate-900 dark:text-slate-100 hover:text-teal-600">
                            {concept.term}
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-600 dark:text-slate-400">
                            {concept.definition}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          {/* Full Dictionary Tab */}
          <TabsContent value="dictionary">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  ABA Dictionary ({filteredDictionary.length} terms)
                </h3>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-[600px] overflow-y-auto">
                {filteredDictionary.map((item, index) => (
                  <motion.div
                    key={item.term}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.01 }}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    data-testid={`dict-term-${index}`}
                  >
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                      {item.term}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      {item.definition}
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 italic pl-4 border-l-2 border-indigo-300 dark:border-indigo-600">
                      {item.citation}
                    </p>
                  </motion.div>
                ))}
                {filteredDictionary.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No terms found matching &ldquo;{searchTerm}&rdquo;</p>
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
        )}

        {/* Stats Footer */}
        {!loadingData && (
        <>
        <motion.div
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 text-center">
            <div className="text-2xl font-bold text-teal-600">{stats.acronyms_count || acronyms.length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Acronyms</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 text-center">
            <div className="text-2xl font-bold text-amber-600">{stats.memory_aids_count || memoryAids.length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Memory Aids</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 text-center">
            <div className="text-2xl font-bold text-violet-600">{stats.core_concepts_count || coreConcepts.reduce((acc, cat) => acc + cat.concepts.length, 0)}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Core Concepts</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 text-center">
            <div className="text-2xl font-bold text-emerald-600">{stats.terms_count || dictionary.length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Dictionary Terms</div>
          </div>
        </motion.div>

        {/* APA References Section */}
        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <BookMarked className="w-5 h-5" />
                References (APA Format)
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                The content in this dictionary is based on the following foundational behavior analysis literature:
              </p>
              
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p className="pl-8 -indent-8">
                  Baer, D. M., Wolf, M. M., & Risley, T. R. (1968). Some current dimensions of applied behavior analysis. <em>Journal of Applied Behavior Analysis, 1</em>(1), 91–97. https://doi.org/10.1901/jaba.1968.1-91
                </p>
                
                <p className="pl-8 -indent-8">
                  Bailey, J. S., & Burch, M. R. (2022). <em>Ethics for behavior analysts</em> (4th ed.). Routledge.
                </p>
                
                <p className="pl-8 -indent-8">
                  Behavior Analyst Certification Board. (2020). <em>Ethics code for behavior analysts</em>. https://www.bacb.com/ethics-code/
                </p>
                
                <p className="pl-8 -indent-8">
                  Behavior Analyst Certification Board. (2022). <em>BCBA/BCaBA task list</em> (6th ed.). https://www.bacb.com/bcba-bcaba-task-list/
                </p>
                
                <p className="pl-8 -indent-8">
                  Cooper, J. O., Heron, T. E., & Heward, W. L. (2020). <em>Applied behavior analysis</em> (3rd ed.). Pearson.
                </p>
                
                <p className="pl-8 -indent-8">
                  Michael, J. (1982). Distinguishing between discriminative and motivational functions of stimuli. <em>Journal of the Experimental Analysis of Behavior, 37</em>(1), 149–155. https://doi.org/10.1901/jeab.1982.37-149
                </p>
                
                <p className="pl-8 -indent-8">
                  Michael, J. (1993). Establishing operations. <em>The Behavior Analyst, 16</em>(2), 191–206. https://doi.org/10.1007/BF03392623
                </p>
                
                <p className="pl-8 -indent-8">
                  Skinner, B. F. (1957). <em>Verbal behavior</em>. Appleton-Century-Crofts.
                </p>
                
                <p className="pl-8 -indent-8">
                  Skinner, B. F. (1953). <em>Science and human behavior</em>. Macmillan.
                </p>
                
                <p className="pl-8 -indent-8">
                  Sundberg, M. L. (2008). <em>VB-MAPP: Verbal behavior milestones assessment and placement program</em>. AVB Press.
                </p>
                
                <p className="pl-8 -indent-8">
                  Sundberg, M. L., & Michael, J. (2001). The benefits of Skinner&apos;s analysis of verbal behavior for children with autism. <em>Behavior Modification, 25</em>(5), 698–724. https://doi.org/10.1177/0145445501255003
                </p>
              </div>
              
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  <strong>Note:</strong> This dictionary is intended as a study aid and quick reference guide. 
                  For comprehensive understanding, students are encouraged to consult the original sources listed above.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
        </>
        )}
      </main>
    </div>
  );
}
